// ===== IMPORTS =====
import { renderMarkdown } from './markdown-renderer.js';
import { processObsidianCitations, quellenZeileMarkieren } from './obsidian-zitate.js';
import { anfrageBody, ndjsonPuffer, quellenMarkdown, hinweiseMarkdown, buddyStrom, schalterLesen, chatStandLesen, chatStandSchreiben, msBisNacht } from './buddy-chat.js';

// Verlauf des Buddy-Chats: nur Nutzer- und Buddy-Züge, nicht aus dem DOM
// gelesen (dort landeten Fehlerblasen als System-Prompt und die aktuelle
// Nachricht doppelt).
let buddyVerlauf = [];
// Sendesperre während eines Stroms; „Verlauf leeren" entwertet die laufende
// Generation, damit die späte Antwort den geleerten Verlauf nicht füllt.
const strom = buddyStrom();

// Der sichtbare Chat wird mitgeschrieben, damit er Schließen und Neuladen
// übersteht (localStorage, je Browser). Weg ist er nur per Reset oder mit
// der Nacht um 03:00.
const CHAT_SPEICHER = 'buddy-chat-stand';
let chatNachrichten = [];
let wiederherstellend = false;

function chatSichern() {
  if (wiederherstellend) return;
  try {
    localStorage.setItem(CHAT_SPEICHER, chatStandSchreiben(chatNachrichten, buddyVerlauf));
  } catch {
    // Ohne localStorage gilt der Chat nur bis zum Neuladen
  }
}

function chatVerwerfen() {
  chatNachrichten = [];
  buddyVerlauf = [];
  strom.leeren();
  try {
    localStorage.removeItem(CHAT_SPEICHER);
  } catch {
    // nichts zu räumen
  }
}

window.buddyVerlaufLeeren = () => { chatVerwerfen(); };

// Frage aus einer Markierung (s. buddy-auswahl.js): Chat aufmachen und
// dieselbe Strecke gehen wie eine getippte Nachricht.
window.buddyFragen = (frage) => {
  if (!frage || !frage.trim()) return;
  window.chatManager?.showChatPanel?.();
  sendMessageWithContext(frage);
};

function sendenSperren(gesperrt) {
  const btn = document.getElementById('send-btn');
  if (btn) btn.disabled = gesperrt;
}

// ===== HILFSFUNKTIONEN =====

// Funktion zum Hinzufügen von Nachrichten zum Chat
function addMessage(message, type = 'ai') {
    const chatMessages = document.getElementById('chat-messages');
    if (!chatMessages) return;

    const messageDiv = document.createElement('div');
    messageDiv.classList.add('message');

    // Äußere Container-Klasse hinzufügen
    if (type === 'user') {
        messageDiv.classList.add('user-message-container');

        // Innere Bubble für Benutzernachrichten erstellen
        const bubbleDiv = document.createElement('div');
        bubbleDiv.classList.add('user-message-bubble');

        // Process command links before setting content
        const processedMessage = processCommandLinks(message);

        // Nachrichteninhalt setzen
        if (processedMessage.includes('<') && processedMessage.includes('>')) {
            bubbleDiv.innerHTML = processedMessage;
        } else {
            bubbleDiv.textContent = processedMessage;
        }

        messageDiv.appendChild(bubbleDiv);
    } else if (type === 'ai') {
        // AI messages get markdown rendering
        messageDiv.classList.add('ai-message');

        // Render markdown to HTML
        const renderedHtml = renderMarkdown(message);
        messageDiv.innerHTML = renderedHtml;

        // Process command links after markdown rendering
        processCommandLinksInElement(messageDiv);

        // Process Obsidian citations and make them clickable
        processObsidianCitations(messageDiv);
        quellenZeileMarkieren(messageDiv);
    } else {
        // Für alle anderen Nachrichtentypen wie bisher
        messageDiv.classList.add(type + '-message');

        // Process command links before setting content
        const processedMessage = processCommandLinks(message);

        if (processedMessage.includes('<') && processedMessage.includes('>')) {
            messageDiv.innerHTML = processedMessage;
        } else {
            messageDiv.textContent = processedMessage;
        }
    }

    chatMessages.appendChild(messageDiv);
    chatMessages.scrollTop = chatMessages.scrollHeight;

    // Nur die drei Chat-Arten sichern; Systemmeldungen der Prozesse nicht.
    if (['user', 'ai', 'error'].includes(type)) {
        chatNachrichten.push({ typ: type, text: message });
        chatSichern();
    }
    return messageDiv;
}

// Add this function right after your addMessage function

function processCommandLinks(text) {
    // Replace Markdown-style links with HTML links
    // Pattern: [Text](cmd://command)
    return text.replace(/\[([^\]]+)\]\(cmd:\/\/([^)]+)\)/g, function(match, text, command) {
        return `<a href="#" class="command-link" data-command="${command}">${text}</a>`;
    });
}

// Process command links in an already rendered HTML element
function processCommandLinksInElement(element) {
    // Find all links with cmd:// protocol
    const links = element.querySelectorAll('a[href^="cmd://"]');

    links.forEach(link => {
        const href = link.getAttribute('href');
        const command = href.replace('cmd://', '');

        // Update link to use command-link class and data attribute
        link.setAttribute('data-command', command);
        link.classList.add('command-link');
        link.setAttribute('href', '#');

        // Prevent default link behavior
        link.addEventListener('click', (e) => {
            e.preventDefault();
            // Command will be handled by existing event listener
        });
    });
}

// Process Obsidian citations in rendered HTML and make them clickable
// Matches patterns like [filename.md:42], path/file.md:123, or just filename.md
// Ersetze die addSystemMessage-Funktion mit dieser verbesserten Version
function addSystemMessage(text) {
    const chatMessages = document.getElementById('chat-messages');
    
    // Container für rechtsbündige Ausrichtung
    const container = document.createElement('div');
    container.className = 'info-message-container';
    
    // Die eigentliche Nachrichtenblase
    const bubble = document.createElement('div');
    bubble.className = 'info-message-bubble';
    bubble.textContent = text;
    
    // Zusammenbauen und einfügen
    container.appendChild(bubble);
    chatMessages.appendChild(container);
    
    // Nach unten scrollen
    chatMessages.scrollTop = chatMessages.scrollHeight;
}

// Hilfsfunktion für Systemnachrichten im Chat
function scrollToBottom() {
    const chatMessages = document.getElementById('chat-messages');
    if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight;
    }
}

function addErrorMessage(message) {
    return addMessage(message, 'error');
}

// Info-Nachrichten rechtsbündig wie User-Nachrichten darstellen
function addInfoMessage(message) {
    const chatMessages = document.getElementById('chat-messages');
    if (!chatMessages) return;
    
    // Container erstellen
    const messageDiv = document.createElement('div');
    messageDiv.classList.add('message', 'info-message-container');
    
    // Bubble für Info-Nachrichten erstellen
    const bubbleDiv = document.createElement('div');
    bubbleDiv.classList.add('info-message-bubble');
    
    // Nachrichteninhalt setzen
    if (message.includes('<') && message.includes('>')) {
        bubbleDiv.innerHTML = message;
    } else {
        bubbleDiv.textContent = message;
    }
    
    messageDiv.appendChild(bubbleDiv);
    chatMessages.appendChild(messageDiv);
    chatMessages.scrollTop = chatMessages.scrollHeight;
    return messageDiv;
}

// Formatiert extrahierte medizinische Codes als HTML
function formatExtractedCodes(data) {
    // Check if data is a string (JSON)
    if (typeof data === 'string') {
        try {
            data = JSON.parse(data);
        } catch (e) {
            console.error("Failed to parse JSON:", e);
            return `<div class="error-message">Error parsing results: ${e.message}</div>`;
        }
    }

    // HTML-Ausgabe aufbauen
    let html = '<div class="extracted-codes">';
    
    // Diagnosen formatieren
    if (data.diagnoses && data.diagnoses.length > 0) {
        html += '<div class="diagnoses-section">';
        html += '<h3>Diagnosen mit ICD-10 Codes</h3>';
        html += '<ul>';
        data.diagnoses.forEach(diagnosis => {
            html += `<li><strong>${diagnosis.text}</strong> - <span class="icd-code">${diagnosis.code}</span> (Confidence: ${diagnosis.confidence})</li>`;
        });
        html += '</ul></div>';
    }
    
    // Prozeduren formatieren
    if (data.procedures && data.procedures.length > 0) {
        html += '<div class="procedures-section">';
        html += '<h3>Prozeduren mit CHOP Codes</h3>';
        html += '<ul>';
        data.procedures.forEach(procedure => {
            html += `<li><strong>${procedure.text}</strong> - <span class="ops-code">${procedure.code}</span> (Confidence: ${procedure.confidence})</li>`;
        });
        html += '</ul></div>';
    }
    
    html += '</div>';
    return html;
}

// Funktion zum Hinzufügen und Entfernen des Chat-Loaders
function addChatLoader() {
    const chatMessages = document.getElementById('chat-messages');
    if (!chatMessages) return null;
    
    // Bestehenden Loader suchen und entfernen
    removeChatLoader();
    
    // Neuen Loader erstellen
    const loaderDiv = document.createElement('div');
    loaderDiv.className = 'message ai-message chat-loader';
    loaderDiv.id = 'chat-loader';
    
    const dotsDiv = document.createElement('div');
    dotsDiv.className = 'dots';
    
    // Drei blinkende Punkte
    for (let i = 0; i < 3; i++) {
        const dot = document.createElement('div');
        dot.className = 'dot';
        dotsDiv.appendChild(dot);
    }
    
    loaderDiv.appendChild(dotsDiv);
    chatMessages.appendChild(loaderDiv);
    chatMessages.scrollTop = chatMessages.scrollHeight;
    
    return loaderDiv;
}

function removeChatLoader() {
    const existingLoader = document.getElementById('chat-loader');
    if (existingLoader) {
        existingLoader.remove();
    }
}

// ===== API-FUNKTIONEN =====

// Globale Variable für den Kontext-Cache
window.contextCache = {
  diagnoses: [],
  procedures: [],
  documentName: null,
  documentId: null
};

// Statuszeile im Lade-Indikator ("Durchsuche Vault…").
function setChatLoaderText(text) {
  const loader = document.getElementById('chat-loader');
  if (!loader) return;
  let el = loader.querySelector('.chat-loader-text');
  if (!el) {
    el = document.createElement('div');
    el.className = 'chat-loader-text';
    loader.appendChild(el);
  }
  el.textContent = text;
}

// Buddy-Chat: schickt den aktuellen Befund mit und liest den NDJSON-Strom
// von /api/buddy/chat (Schritte, dann Antwort oder Fehler).
async function sendMessageWithContext(message, displayMessage = true) {
  const generation = strom.starten();
  if (generation === null) return; // ein Strom läuft schon
  sendenSperren(true);
  if (displayMessage) {
    addMessage(message, 'user');
  }
  addChatLoader();
  const befund = window.editorAPI?.getText?.() ?? '';
  const body = anfrageBody(message, befund, buddyVerlauf, buddySchalter);

  try {
    const response = await fetch('/api/buddy/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      let text = `Server antwortete mit ${response.status}`;
      try {
        const fehler = await response.json();
        if (fehler.error) text = fehler.error;
      } catch (_) { /* kein JSON */ }
      throw new Error(text);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const puffer = ndjsonPuffer();
    let beendet = false;

    const verarbeite = (ereignis) => {
      // Verlauf wurde zwischenzeitlich geleert: nichts mehr anzeigen.
      if (!strom.gilt(generation)) return;
      if (ereignis.typ === 'schritt') {
        setChatLoaderText(ereignis.text);
      } else if (ereignis.typ === 'antwort') {
        removeChatLoader();
        addMessage(
          ereignis.text + hinweiseMarkdown(ereignis.hinweise) + quellenMarkdown(ereignis.belege),
          'ai'
        );
        buddyVerlauf = [
          ...buddyVerlauf,
          { rolle: 'nutzer', text: message },
          { rolle: 'buddy', text: ereignis.text },
        ];
        chatSichern();
        beendet = true;
      } else if (ereignis.typ === 'fehler') {
        removeChatLoader();
        addErrorMessage(ereignis.text);
        // Bis zum Abbruch gefundene Quellen nicht verwerfen (Spec, Rückfallwege).
        if (ereignis.belege && ereignis.belege.length > 0) {
          addMessage(`Bis dahin gefunden:${quellenMarkdown(ereignis.belege)}`, 'ai');
        }
        beendet = true;
      }
    };

    for (;;) {
      if (!strom.gilt(generation)) {
        // Verlauf geleert: Strom abbrechen, der Server sieht den fehlenden
        // Empfänger und ruft kein Modell mehr.
        await reader.cancel().catch(() => {});
        return;
      }
      const { value, done } = await reader.read();
      if (done) break;
      puffer.zeilen(decoder.decode(value, { stream: true })).forEach(verarbeite);
    }
    puffer.ende().forEach(verarbeite);

    if (!beendet && strom.gilt(generation)) {
      removeChatLoader();
      addErrorMessage('Die Verbindung brach ab, bevor eine Antwort kam.');
    }
  } catch (error) {
    if (strom.gilt(generation)) {
      removeChatLoader();
      addErrorMessage(`Fehler: ${error.message}`);
    }
  } finally {
    strom.beenden();
    sendenSperren(false);
  }
}

// Funktion zum Aktualisieren des Kontexts nach der Backend-Verarbeitung
function updateContextFromBackendResponse(data) {
  if (data.diagnoses && data.diagnoses.length > 0) {
    data.diagnoses.forEach(diagnosis => {
      // Only add if not already in cache
      if (!window.contextCache.diagnoses.some(d => d.code === diagnosis.code)) {
        window.contextCache.diagnoses.push(diagnosis);
      }
    });
  }
  
  if (data.procedures && data.procedures.length > 0) {
    data.procedures.forEach(procedure => {
      // Only add if not already in cache
      if (!window.contextCache.procedures.some(p => p.code === procedure.code)) {
        window.contextCache.procedures.push(procedure);
      }
    });
  }
}

// Enhanced function to handle multi-step processing workflow for medical document analysis
function sendBackendProcessRequest(command, displayMessage) {
  // Show user message if provided
  if (displayMessage) {
    addMessage(displayMessage, 'user');
  }
  
  // Show typing indicator
  addChatLoader();
  
  // Get the document ID from the window object where it's actually stored
  const docId = window.currentDocumentId;
  
  if (!docId) {
    removeChatLoader();
    addErrorMessage("Kein Dokument ausgewählt. Bitte zuerst ein Dokument hochladen.");
    return;
  }
  
  // Determine the workflow based on the command
  let workflow;
  if (command === "process_document" || command.includes("kodier")) {
    // Two-step workflow: extract + code
    workflow = "extract_and_code";
  } else if (command === "extract_diagnoses") {
    workflow = "extract_diagnoses";
  } else if (command === "extract_procedures") {
    workflow = "extract_procedures";
  } else {
    workflow = "extract_all";
  }
  
  // Set body with command, document ID and session ID
  const bodyData = {
    workflow: workflow,
    document_id: docId,
    session_id: window.sessionId,
    user_message: displayMessage
  };
  
  // Send the request to backend
  fetch('/api/backend-process', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(bodyData)
  })
  .then(response => {
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return response.json();
  })
  .then(data => {
    // Remove typing indicator
    removeChatLoader();
    
    // Check for structured data in the main response
    if (data.diagnoses || data.procedures) {
      // If we have direct structured data, use it
      let formattedResponse = formatExtractedCodes(data);
      addMessage(formattedResponse, 'ai');
      updateContextFromBackendResponse(data);
    } 
    else if (data.response) {
      // If we have a response field, it might be:
      // 1. A plain text/markdown string
      // 2. A JSON string that needs parsing
      
      // First, remove the closing message if present
      let responseContent = data.response;
      responseContent = responseContent.replace(/\n*Die Dokumentenverarbeitung wurde abgeschlossen\.*$/g, '');
      
      // Try to see if it's JSON
      try {
        const parsedResponse = JSON.parse(responseContent);
        // If we got here, it's valid JSON
        if (parsedResponse.diagnoses || parsedResponse.procedures) {
          updateContextFromBackendResponse(parsedResponse);
          let formattedResponse = formatExtractedCodes(parsedResponse);
          addMessage(formattedResponse, 'ai');
        } else {
          // It's JSON but not diagnoses/procedures format
          addMessage(convertMarkdownToHtml(responseContent), 'ai');
        }
      } catch (e) {
        // Not JSON, treat as markdown formatted text
        addMessage(convertMarkdownToHtml(responseContent), 'ai');
      }
    } else {
      // Fallback message if no structured data or response
      addMessage("Die Ergebnisse der Dokumentenanalyse wurden abgeschlossen.", 'ai');
    }
  })
  .catch(error => {
    removeChatLoader();
    addErrorMessage(`Fehler bei der Verarbeitung: ${error.message}`);
  });
}

// Helper function to resize textarea and toggle single-line class
function resizeTextarea(textarea) {
    if (!textarea) return;

    const wrapper = textarea.closest('.chat-input-wrapper');
    if (!wrapper) return;

    // Temporarily force multi-line padding to measure true content height
    // This prevents oscillation caused by width changes
    const wasInSingleLine = wrapper.classList.contains('single-line');
    wrapper.classList.remove('single-line');

    // Measure with multi-line (wider) padding
    textarea.style.height = '50px';
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.max(50, Math.min(scrollHeight, 200));

    // Decide mode based on multi-line measurement
    const shouldBeSingleLine = newHeight <= 55;

    // Apply the correct mode
    if (shouldBeSingleLine) {
        wrapper.classList.add('single-line');
    }
    // else: already removed single-line above

    // Now measure again with correct padding and set final height
    textarea.style.height = '50px';
    const finalHeight = Math.max(50, Math.min(textarea.scrollHeight, 200));
    textarea.style.height = finalHeight + 'px';
    textarea.style.overflowY = (textarea.scrollHeight > 200) ? 'auto' : 'hidden';
}

// Funktion zum Senden einer Chat-Nachricht
async function sendMessage() {
    if (strom.laeuft()) return; // Eingabe bleibt stehen, bis der Strom endet
    const userInput = document.getElementById('user-input');
    const message = userInput.value.trim();
    if (!message) return;

    // Clear input immediately
    userInput.value = '';
    resizeTextarea(userInput);

    // Text-Nachricht anzeigen
    addMessage(message, 'user');

    // Nachricht senden
    sendMessageWithContext(message, false);
}

// ===== BUDDY-SCHALTER (Befund / Vault / Literatur) =====

const SCHALTER_SPEICHER = 'buddy-schalter';
let buddySchalter = schalterLesen(null);
try {
  buddySchalter = schalterLesen(localStorage.getItem(SCHALTER_SPEICHER));
} catch {
  // ohne localStorage gilt der Default: alle an
}

// Pfeil links im Eingabefeld öffnet ein Menü mit drei Häkchen-Einträgen.
function schalterEinrichten() {
  const toggle = document.getElementById('buddy-kontext-toggle');
  const menu = document.getElementById('buddy-kontext-menu');
  if (!toggle || !menu) return;
  const eintraege = menu.querySelectorAll('.buddy-kontext-eintrag');

  const zeigen = () => {
    eintraege.forEach((e) => {
      const an = buddySchalter[e.dataset.schalter];
      e.classList.toggle('aktiv', an);
      e.setAttribute('aria-checked', String(an));
    });
    // Punkt am Pfeil, sobald nicht mehr alles an ist
    toggle.classList.toggle('eingeschraenkt', !Object.values(buddySchalter).every(Boolean));
  };
  const oeffnen = (offen) => {
    menu.classList.toggle('offen', offen);
    toggle.setAttribute('aria-expanded', String(offen));
  };

  zeigen();
  toggle.addEventListener('click', (ev) => {
    ev.stopPropagation();
    oeffnen(!menu.classList.contains('offen'));
  });
  eintraege.forEach((e) => {
    e.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const name = e.dataset.schalter;
      buddySchalter = { ...buddySchalter, [name]: !buddySchalter[name] };
      zeigen();
      try {
        localStorage.setItem(SCHALTER_SPEICHER, JSON.stringify(buddySchalter));
      } catch {
        // Zustand gilt dann nur bis zum Neuladen
      }
    });
  });
  document.addEventListener('click', (ev) => {
    if (!menu.contains(ev.target)) oeffnen(false);
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && menu.classList.contains('offen')) {
      oeffnen(false);
      toggle.focus();
    }
  });
}

// Gespeicherten Chat zurück in die Seite holen (ohne ihn erneut zu sichern).
function chatWiederherstellen() {
  let stand = null;
  try {
    stand = chatStandLesen(localStorage.getItem(CHAT_SPEICHER));
  } catch {
    stand = null;
  }
  if (!stand) {
    try {
      localStorage.removeItem(CHAT_SPEICHER);
    } catch {
      // nichts zu räumen
    }
    return;
  }
  wiederherstellend = true;
  try {
    stand.nachrichten.forEach((n) => addMessage(n.text, n.typ));
  } finally {
    wiederherstellend = false;
  }
  chatNachrichten = stand.nachrichten;
  buddyVerlauf = stand.verlauf;
}

// Um 03:00 ist der Chat von gestern; bleibt die Seite offen, räumt der Timer.
function nachtgrenzeStellen() {
  setTimeout(() => {
    const chatMessages = document.getElementById('chat-messages');
    if (chatMessages) chatMessages.innerHTML = '';
    chatVerwerfen();
    nachtgrenzeStellen();
  }, msBisNacht());
}

// ===== DOM SETUP =====

document.addEventListener('DOMContentLoaded', function() {
    // Generate or load persistent session ID
    let sid = localStorage.getItem('sessionId');
    if (!sid) {
        sid = crypto.randomUUID();
        localStorage.setItem('sessionId', sid);
    }
    window.sessionId = sid;
    
    schalterEinrichten();
    chatWiederherstellen();
    nachtgrenzeStellen();

    // Elemente referenzieren
    const sendBtn = document.getElementById('send-btn');
    const userInput = document.getElementById('user-input');

    // Send-Button Event-Handler
    if (sendBtn) {
        sendBtn.addEventListener('click', sendMessage);

        // Enter to send (Shift+Enter for newline)
        if (userInput) {
            userInput.addEventListener('keydown', function(e) {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                }
            });
        }
    }

    // ===== Scrollbar-Steuerung =====
    const chatMessages = document.getElementById('chat-messages');
    if (chatMessages) {
        let scrollTimer;
        
        // Bei Scroll-Ereignis
        chatMessages.addEventListener('scroll', function() {
            chatMessages.classList.add('scrolling');
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => {
                chatMessages.classList.remove('scrolling');
            }, 800);
        });
        
        // Bei Mausrad-Ereignis (wichtig für manche Browser)
        chatMessages.addEventListener('wheel', function() {
            chatMessages.classList.add('scrolling');
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => {
                chatMessages.classList.remove('scrolling');
            }, 800);
        }, { passive: true });
    }

    // View-Toggler - Überarbeitet, um nur noch für 'editor' die Container-Klasse zu ändern
    const appContainer = document.querySelector('.app-container');
    function setView(view) {
        // Nur noch für 'editor' die Container-Klasse ändern
        if (view === 'editor') {
            appContainer.classList.remove('view-split','view-chat');
            appContainer.classList.add('view-editor');
        }
        
        // Button-Sichtbarkeiten weiterhin konsistent handhaben
        const openChatBtn = document.getElementById('btn-open-chat');
        const splitBtn = document.getElementById('btn-chat-splitscreen');
        const fullscreenBtn = document.getElementById('btn-chat-fullscreen');
        
        // Handle button visibility based on view
        if (view === 'editor') {
            if (openChatBtn) openChatBtn.style.display = 'flex';
        } else {
            if (openChatBtn) openChatBtn.style.display = 'none';
        }
        
        // Handle split-screen toggle button visibility
        if (view === 'chat' && splitBtn) {
            splitBtn.style.display = 'flex';
        } else if (splitBtn) {
            splitBtn.style.display = 'none';
        }
        
        // Handle fullscreen toggle button visibility
        if (view === 'split' && fullscreenBtn) {
            fullscreenBtn.style.display = 'flex';
        } else if (fullscreenBtn) {
            fullscreenBtn.style.display = 'none';
        }
    }
    
    // Default: nur Editor anzeigen
    setView('editor');
    
    // Auto-resize textarea (max 200px height)
    const textarea = document.getElementById('user-input');
    if (textarea) {
        // Event und Initialisierung
        textarea.addEventListener('input', () => resizeTextarea(textarea));
        resizeTextarea(textarea);
    }
});

// WICHTIG: Keine doppelten Scrollbar-Event-Handler hier mehr hinzufügen!

// ===== GLOBALE FEHLERBEHANDLUNG =====

// JavaScript-Fehler abfangen
window.onerror = function(message, source, line, column, error) {
    addErrorMessage(`JavaScript-Fehler: ${message}`);
    return false;
};

// Unbehandelte Promise-Fehler abfangen
window.addEventListener('unhandledrejection', function(event) {
    addErrorMessage(`Unbehandelter Promise-Fehler: ${event.reason}`);
});

// Sicherer Event-Listener-Wrapper
function safeAddEventListener(selector, event, handler) {
    const element = document.getElementById(selector);
    if (element) {
        element.addEventListener(event, handler);
    } else {
        console.warn(`Element mit ID '${selector}' nicht gefunden - Event-Listener nicht hinzugefügt`);
    }
}

// Safe event listener wrapper for better error handling

// Command Link Handler - VERBESSERTE VERSION mit direkter Event-Bindung auf dem Basis-Dokument
document.addEventListener('DOMContentLoaded', function() {
  // Globalen Event-Handler für Command-Links registrieren
  document.addEventListener('click', function(e) {
    // Prüfen, ob ein Command-Link geklickt wurde (entweder über href oder data-attribute)
    const link = e.target.closest('a.command-link') || e.target.closest('a[href^="cmd://"]');
    if (link) {
      e.preventDefault(); // Standard-Link-Verhalten verhindern
      
      // Command aus href oder data-attribute extrahieren
      let command;
      if (link.classList.contains('command-link')) {
        command = link.getAttribute('data-command');
      } else {
        command = link.href.replace('cmd://', '');
      }
      
      // Verschiedene Backend-Commands verarbeiten
      const displayText = link.textContent || '';
      switch(command) {
        case 'process_document':
          sendBackendProcessRequest("process_document", "Kodiere die Diagnosen und Prozeduren in diesem Dokument");
          break;
        case 'extract_diagnoses':
          sendBackendProcessRequest("extract_diagnoses", "Extrahiere die Diagnosen in diesem Dokument");
          break;
        case 'extract_procedures':
          sendBackendProcessRequest("extract_procedures", "Extrahiere die Prozeduren in diesem Dokument");
          break;
        case 'kodiere_diagnosen':
          sendBackendProcessRequest("kodiere_diagnosen", "Kodiere die Diagnosen in diesem Dokument");
          break;
        case 'kodiere_prozeduren':
          sendBackendProcessRequest("kodiere_prozeduren", "Kodiere die Prozeduren in diesem Dokument");
          break;
        case 'index_and_store':
          sendIndexDocumentRequest("Indexiere und speichere dieses Dokument");
          break;
        case 'search_documents':
          // Use the link text as search query
          sendSearchDocumentRequest(displayText);
          break;
        default:
          console.error('Unbekannter Command:', command);
      }
    }
  });
});

// New function to handle indexing and storing documents
function sendIndexDocumentRequest(displayMessage) {
    // Show the user's message in the chat (for context)
    addMessage(displayMessage, 'user');
    
    // Show a loading indicator
    const loader = addChatLoader();
    
    // Get current document ID from the global variable
    const docId = window.currentDocumentId;
    if (!docId) {
        removeChatLoader();
        addErrorMessage("Kein Dokument gefunden. Bitte laden Sie zuerst ein Dokument hoch.");
        return;
    }
    
    // Make a direct API call to the index-document endpoint
    fetch('/api/index-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            document_id: docId
        }),
    })
    .then(response => {
        if (!response.ok) {
            throw new Error('Indexing and storing failed with status: ' + response.status);
        }
        return response.json();
    })
    .then(data => {
        removeChatLoader();
        
        // Wichtig: Neue Dokument-ID aus der Serverantwort speichern, falls vorhanden
        if (data.document_id) {
            window.currentDocumentId = data.document_id;
        }
        
        if (data.message) {
            addMessage(data.message, 'ai');
        } else if (data.status === 'success') {
            addMessage(`Document ${data.document_id || 'unknown'} successfully indexed`, 'ai');
        }
    })
    .catch(error => {
        removeChatLoader();
        addErrorMessage(`Fehler beim Indexieren und Speichern: ${error.message}`);
    });
}

// New function to search documents via vector_search tool
function sendSearchDocumentRequest(displayMessage) {
    // Display user's query in chat
    addMessage(displayMessage, 'user');
    // Show loader
    const loader = addChatLoader();
    // Build request payload
    const payload = {
        tool_name: 'vector_search',
        params: { query: displayMessage, limit: 5 }  // This is already correct
    };
    fetch('/api/mcp/tools/call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    })
    .then(response => {
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
        return response.json();
    })
    .then(data => {
        removeChatLoader();
        // data is an array of results
        let html = '<div class="search-results"><h3>Suchergebnisse:</h3><ul>';
        data.forEach(item => {
            html += `<li><strong>${item.id}</strong>: ${item.content} (Score: ${item.score.toFixed(2)})</li>`;
        });
        html += '</ul></div>';
        addMessage(html, 'ai');
    })
    .catch(error => {
        removeChatLoader();
        addErrorMessage(`Search error: ${error.message}`);
    });
}

// Function to display medical coding results
function displayMedicalCodingResults(data) {
  // Create container for the structured medical results
  const resultDiv = document.createElement('div');
  resultDiv.className = 'ai-message';
  
  // Create heading
  const heading = document.createElement('h3');
  heading.textContent = 'Medizinische Kodierung';
  resultDiv.appendChild(heading);
  
  // Handle diagnoses if available
  if (data.diagnoses && data.diagnoses.length > 0) {
    const diagnosesSection = document.createElement('div');
    diagnosesSection.className = 'extracted-codes';
    
    const diagnosesHeading = document.createElement('h4');
    diagnosesHeading.textContent = 'Diagnosen';
    diagnosesSection.appendChild(diagnosesHeading);
    
    const diagnosisList = document.createElement('ul');
    data.diagnoses.forEach(diagnosis => {
      const item = document.createElement('li');
      const codeSpan = document.createElement('span');
      codeSpan.className = 'icd-code';
      codeSpan.textContent = diagnosis.code || 'Kein Code';
      
      item.textContent = `${diagnosis.text} `;
      item.appendChild(codeSpan);
      diagnosisList.appendChild(item);
    });
    
    diagnosesSection.appendChild(diagnosisList);
    resultDiv.appendChild(diagnosesSection);
  }
  
  // Handle procedures if available
  if (data.procedures && data.procedures.length > 0) {
    const proceduresSection = document.createElement('div');
    proceduresSection.className = 'extracted-codes';
    
    const proceduresHeading = document.createElement('h4');
    proceduresHeading.textContent = 'Prozeduren';
    proceduresSection.appendChild(proceduresHeading);
    
    const proceduresList = document.createElement('ul');
    data.procedures.forEach(procedure => {
      const item = document.createElement('li');
      const codeSpan = document.createElement('span');
      codeSpan.className = 'ops-code';
      codeSpan.textContent = procedure.code || 'Kein Code';
      
      item.textContent = `${procedure.text} `;
      item.appendChild(codeSpan);
      proceduresList.appendChild(item);
    });
    
    proceduresSection.appendChild(proceduresList);
    resultDiv.appendChild(proceduresSection);
  }
  
  // Add to chat
  const messageDiv = addMessage('', 'ai');
  messageDiv.appendChild(resultDiv);
  scrollToBottom();
}